import {
  Input,
  InputNumber,
  Select,
  DatePicker,
  Row,
  Card,
  Button,
  Form,
  Space,
  Alert,
} from "antd";
import { CfdiComplementModel } from "api/generated";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { ExtraInfoBtn } from "common/helpers/generalUtils";
import {
  patternPermiso,
  patternRFC,
  uuidPattern,
  validateCantidadMonetaria,
  validateValorNumerico,
} from "services/utils/patterns";
import { PlusOutlined } from "@ant-design/icons";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import { useEffect } from "react";
import { useIsMobile } from "hooks/useIsMobile";
import dayjs from "dayjs";

interface CfdiFormProps {
  editingData?: Partial<CfdiComplementModel>;
  parentType: COMPLEMENT_PARENT;
  onSubmit: (data: Partial<CfdiComplementModel>) => void;
  onCancelEdit: () => void;
  isEditing?: boolean;
}

export function CfdiForm({
  editingData,
  parentType,
  onSubmit,
  onCancelEdit,
  isEditing = false,
}: CfdiFormProps) {
  const [singleCFDIForm] = Form.useForm<
    CfdiComplementModel & { invoiceDateDay?: dayjs.Dayjs }
  >();

  const { isMobile } = useIsMobile();

  // Inicializar formulario con datos de edición o valores por defecto
  useEffect(() => {
    if (editingData) {
      // Si estamos editando y tenemos invoiceDate como Dayjs, usarla directamente
      const formData = {
        ...editingData,
        invoiceDateDay: dayjs(editingData.invoiceDate),
      };
      singleCFDIForm.setFieldsValue(formData);
    } else {
      singleCFDIForm.resetFields();
      singleCFDIForm.setFieldsValue({ cfdiRegisterType: parentType });
    }
  }, [editingData, singleCFDIForm, parentType]);

  function handleSubmit() {
    singleCFDIForm
      .validateFields()
      .then((values) => {
        const cfdiData: Partial<CfdiComplementModel> = {
          ...values,
          cfdiRegisterType: parentType,
          invoiceDate: values.invoiceDateDay
            ? values.invoiceDateDay.format("YYYY-MM-DD HH:mm:ss")
            : undefined,
        };

        onSubmit(cfdiData);

        // Limpiar formulario después de agregar (no en edición)
        if (!isEditing) {
          singleCFDIForm.resetFields();
          singleCFDIForm.setFieldsValue({ cfdiRegisterType: parentType });
        }
      })
      .catch((error) => {
        console.error("Error validando formulario:", error);
      });
  }

  return (
    <Card
      title={isEditing ? "Editando CFDI" : "Agregar Nuevo CFDI"}
      extra={
        <Space>
          <Space.Compact
            direction={isMobile ? "vertical" : "horizontal"}
            size="large"
          >
            {isEditing && (
              <Button onClick={onCancelEdit} size="large">
                Cancelar Edición
              </Button>
            )}
            <Button
              type="primary"
              variant="solid"
              color={isEditing ? "danger" : undefined}
              icon={<PlusOutlined />}
              onClick={handleSubmit}
              size="large"
            >
              {isEditing ? "Actualizar CFDI" : "Agregar CFDI"}
            </Button>
          </Space.Compact>
        </Space>
      }
    >
      <Form
        form={singleCFDIForm}
        component={false}
        layout="vertical"
        initialValues={{ cfdiRegisterType: parentType }}
      >
        <FormItem<CfdiComplementModel> hidden name="id" />
        <FormItem<CfdiComplementModel> hidden name="invoiceDate" />
        {isEditing && (
          <Row gutter={24}>
            <ResponsiveCol size="xl">
              <Alert
                message="Folio Fiscal no editable"
                description="El campo UUID SAT no es editable, el proceso de reemplazo consituye en su eliminación, a continuación agrege uno nuevo con los datos correctos."
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            </ResponsiveCol>
          </Row>
        )}
        <Row gutter={24}>
          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Tipo de transacción"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
              name="cfdiRegisterType"
            >
              <Select
                disabled
                placeholder="Selecciona el tipo de transacción"
                suffixIcon={ExtraInfoBtn("Tipo de transacción", [
                  "Debes registrar a que tipo de transacción sera asociado este complemento, las opciones son VENTA, TRASPASO o RECEPCION",
                ])}
              >
                <Select.Option value="RECEPCION">RECEPCION</Select.Option>
                <Select.Option value="VENTA">VENTA</Select.Option>
              </Select>
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="UUID SAT"
              name="fiscalId"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                { pattern: uuidPattern, message: "UUID no válido" },
              ]}
              normalize={(inputValue) => inputValue?.toUpperCase()}
            >
              <Input
                placeholder="UUID SAT"
                disabled={isEditing}
                addonAfter={ExtraInfoBtn("Folio UUID del CFDI.", [
                  "Debes registrar el folio UUID del CFDI.",
                  "CFDI = 4FE29D07-F126-4038-AC77-C60A33646FF9",
                ])}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Tipo de CFDI"
              name="cfdiType"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Select
                placeholder="Selecciona el tipo CFDI"
                suffixIcon={ExtraInfoBtn("Tipo de CFDI", [
                  "Debes registrar el tipo de CFDI, es decir, si se trata de uno de tipo Ingreso, Egreso o Traslado.",
                  "Ejemplo: TipoCFDI = Ingreso",
                ])}
              >
                <Select.Option value="INGRESO">Ingreso</Select.Option>
                <Select.Option value="EGRESO">Egreso</Select.Option>
                <Select.Option value="TRASLADO">Traslado</Select.Option>
              </Select>
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="RFC Receptor"
              name="rfcReceptor"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                { pattern: patternRFC, message: "RFC no válido" },
              ]}
              normalize={(inputValue) => inputValue?.toUpperCase()}
            >
              <Input
                placeholder="RFC Receptor"
                addonAfter={ExtraInfoBtn("RFC Receptor", [
                  "Debes registrar la clave en el RFC del cliente a quien emites el CFDI o del proveedor de quien recibes el CFDI.",
                  "Ejemplo: Si se trata de una persona física se debe registrar: RfcClienteOProveedor = CABL840215RF4",
                ])}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Nombre Receptor"
              name="nameReceptor"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                {
                  max: 150,
                  message: "No se puede exceder los 150 caracteres.",
                },
              ]}
              normalize={(inputValue) => inputValue?.toUpperCase()}
            >
              <Input
                placeholder="Nombre Receptor"
                addonAfter={ExtraInfoBtn(
                  "Nombre, denominación o razón social",
                  [
                    "Debes registrar el nombre, denominación o razón social del cliente a quien emites el CFDI o del proveedor de quien recibes el CFDI.",
                    "Ejemplo: NombreClienteOProveedor = Petromexico S.A. de C.V",
                  ]
                )}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="RFC Emisor"
              name="rfcEmisor"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                { pattern: patternRFC, message: "RFC no válido" },
              ]}
              normalize={(inputValue) => inputValue?.toUpperCase()}
            >
              <Input
                placeholder="RFC Emisor"
                addonAfter={ExtraInfoBtn("RFC Emisor", [
                  "Debes registrar la clave en el RFC del cliente a quien emites el CFDI o del proveedor de quien recibes el CFDI.",
                  "Ejemplo: Si se trata de una persona física se debe registrar: RfcClienteOProveedor = CABL840215RF4",
                ])}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Nombre Emisor"
              name="nameEmisor"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                {
                  max: 150,
                  message: "No se puede exceder los 150 caracteres.",
                },
              ]}
              normalize={(inputValue) => inputValue?.toUpperCase()}
            >
              <Input
                placeholder="Nombre Emisor"
                addonAfter={ExtraInfoBtn(
                  "Nombre, denominación o razón social",
                  [
                    "Debes registrar el nombre, denominación o razón social del cliente a quien emites el CFDI o del proveedor de quien recibes el CFDI.",
                    "Ejemplo: NombreClienteOProveedor = Petromexico S.A. de C.V",
                  ]
                )}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Permiso"
              name="permission"
              rules={[
                { required: false, message: "Este campo es obligatorio" },
                {
                  pattern: patternPermiso,
                  message: "El valor ingresado no tiene un formato valido",
                },
              ]}
            >
              <Input
                placeholder="Permiso"
                addonAfter={ExtraInfoBtn("Número de permiso", [
                  "La presencia de este elemento es opcional y debes registrar el número de permiso otorgado por la Comisión Reguladora de Energía a tu proveedor o a tus clientes de gas natural o petrolíferos.",
                  "Ejemplo: PermisoClienteOProveedor = H/88888/COM/2021",
                ])}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Precio"
              name="price"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                {
                  validator: validateCantidadMonetaria,
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                precision={6}
                prefix="$"
                suffix="mxn"
                placeholder="Precio de Venta o Compra"
                addonAfter={ExtraInfoBtn("Precio de Venta o Compra", [
                  "Debes registrar el precio de Venta o Compra",
                  "Es la cantidad de pesos mexicanos mediante la cual un permisionario de distribución adjudica o adquiere un petrolífero. Incluye impuestos, pero no la tarifa de transporte ni almacenamiento.",
                  "Ejemplo: PrecioVentaOCompraOContrap = 1380,000.00000",
                ])}
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem
              label="Fecha Factura"
              name="invoiceDateDay"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <DatePicker
                style={{ width: "100%" }}
                showTime
                format="YYYY-MM-DD HH:mm:ss"
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Volumen"
              name="volume"
              rules={[
                { required: true, message: "Este campo es obligatorio" },
                {
                  validator: validateValorNumerico,
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="VolumenDocumentado"
                precision={6}
                addonAfter={ExtraInfoBtn("Volumen facturado", [
                  "Debes registrar el valor adimensional del volumen, es decir, representar la cantidad del volumen facturado.",
                  "ValorNumerico= 20000.00000",
                ])}
                suffix="Litros"
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Monto"
              name="amount"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <InputNumber
                prefix="$"
                suffix="mxn"
                precision={6}
                style={{ width: "100%" }}
                placeholder="Monto"
              />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Estatus"
              name="status"
              rules={[{ required: true, message: "Este campo es obligatorio" }]}
            >
              <Select placeholder="Selecciona el estatus del CFDI">
                <Select.Option value="Vigente">Vigente</Select.Option>
                <Select.Option value="Cancelado">Cancelado</Select.Option>
              </Select>
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol>
            <FormItem<CfdiComplementModel>
              label="Número de Identificación"
              name="numberId"
            >
              <Input placeholder="Número de Identificación" />
            </FormItem>
          </ResponsiveCol>

          <ResponsiveCol size="xl">
            <FormItem<CfdiComplementModel>
              label="Descripción"
              name="description"
            >
              <Input.TextArea placeholder="Descripción" rows={3} />
            </FormItem>
          </ResponsiveCol>
        </Row>
      </Form>
    </Card>
  );
}
